/* hooks/useBackNav.jsx — единая логика кнопки «Назад» с надёжным fallback (S13, §2.3).
   Проблема (п.38, §15.5 инвентаря): handleBack опирался на window.history.state?.idx —
   ненадёжно при прямом заходе (deep-link) или в iframe-лаунчере → пользователь «застревал».
   Решение: если внутри приложения уже были переходы (window.__rrNavDepth>0) → navigate(-1);
   иначе — на безопасный fallback (корень раздела), который НИКОГДА не blank/ошибка.

   API: const goBack = useBackNav(explicitFallback?);  // → function
   explicitFallback переопределяет вычисленный по таблице маршрутов.
   ХЕНДОФФ → frontend/src/hooks/useBackNav.js: заменяет ad-hoc navigate(-1) в
   PageHeader / KitchenBreadcrumb / ReportDetail. */

import React from 'react';
import { RR } from '../lib/router.jsx';

  const { useCallback } = React;
  const { useNavigate, useLocation } = RR;

  // Таблица fallback по текущему маршруту (§2.3). Возвращает безопасный корень раздела.
  function deriveFallback(pathname, search) {
    const sp = new URLSearchParams(search || '');

    // Кухня с открытым drawer-стеком → закрыть стек (на активную вкладку)
    if (pathname === '/kitchen' && sp.get('edit')) {
      const tab = sp.get('tab') || 'recipes';
      return '/kitchen?tab=' + tab;
    }

    // Детальная отчёта → список отчётов
    if (/^\/reports\/[^/]+$/.test(pathname)) return '/reports';

    // Правовые заглушки публичны (доступны гостю): безопасный fallback — вход,
    // а не /chat (он под RequireAuth увёл бы гостя на /signin без контекста, фикс №32).
    if (pathname.startsWith('/legal/')) return '/signin';

    // Уведомления → корень (Ассистент)
    if (pathname === '/notifications') return '/chat';

    // Любая вкладка настроек → корень настроек
    if (pathname === '/settings' && sp.get('tab')) return '/settings';

    // Checkout → к тарифам
    if (pathname.startsWith('/checkout/')) return '/settings?tab=subscription';

    // По умолчанию — Ассистент
    return '/chat';
  }

  function useBackNav(explicitFallback) {
    const navigate = useNavigate();
    const location = useLocation();
    return useCallback(() => {
      const fallback = explicitFallback || deriveFallback(location.pathname, location.search);
      if ((window.__rrNavDepth || 0) > 0) navigate(-1);
      else navigate(fallback);
    }, [navigate, location.pathname, location.search, explicitFallback]);
  }

export { useBackNav };
export { deriveFallback as deriveBackFallback };
